import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import i18n from "i18next";
import { springs } from "../motion";
import {
  ActivityTimelineConnector,
  ActivityTimelineDot,
} from "./ActivityTimeline";
import type { ActivityEvent, EventTypeRenderer } from "./types";

// ─── Helpers ────────────────────────────────────────────────────────────────

export function formatRelativeTime(date: Date): string {
  const now = Date.now();
  const diff = now - date.getTime();
  const minutes = Math.floor(diff / 60_000);
  const hours = Math.floor(diff / 3_600_000);
  const days = Math.floor(diff / 86_400_000);

  if (minutes < 1) return i18n.t("common:time.justNow");
  if (minutes < 60) return i18n.t("common:time.minutesAgo", { count: minutes });
  if (hours < 24) return i18n.t("common:time.hoursAgo", { count: hours });
  if (days === 1) return i18n.t("common:time.yesterday");
  if (days < 7) return i18n.t("common:time.daysAgo", { count: days });
  return date.toLocaleDateString(i18n.language, {
    month: "short",
    day: "numeric",
  });
}

/** Detect a status transition in metadata (e.g. { from: "draft", to: "submitted" }) */
function getStatusTransition(
  metadata: Record<string, string> | undefined,
): { from: string; to: string } | null {
  if (!metadata) return null;
  const from = metadata.from ?? metadata.previousStatus ?? metadata.oldStatus;
  const to = metadata.to ?? metadata.newStatus ?? metadata.status;
  if (from && to && from !== to) return { from, to };
  return null;
}

/** Remaining metadata entries after removing transition keys */
function getExtraMetadata(
  metadata: Record<string, string> | undefined,
): [string, string][] {
  if (!metadata) return [];
  const skip = new Set([
    "from",
    "to",
    "previousStatus",
    "newStatus",
    "oldStatus",
    "status",
  ]);
  return Object.entries(metadata).filter(([k]) => !skip.has(k));
}

// ─── ActivityItem ───────────────────────────────────────────────────────────

interface ActivityItemProps {
  event: ActivityEvent;
  index: number;
  isLast: boolean;
  /** Per-event-type renderer override from the entity config */
  renderer?: EventTypeRenderer;
  /** Field display labels for diff rows, e.g. { displayName: "Name" } */
  fieldLabels?: Record<string, string>;
}

export function ActivityItem({
  event,
  index,
  isLast,
  renderer,
  fieldLabels,
}: ActivityItemProps) {
  const transition = getStatusTransition(event.metadata);
  const extraMeta = getExtraMetadata(event.metadata);

  return (
    <motion.div
      className="flex gap-3 group"
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -4 }}
      transition={{ ...springs.smooth, delay: index * 0.04 }}
    >
      {/* Timeline column */}
      <div className="flex flex-col items-center">
        <ActivityTimelineDot
          category={event.category}
          index={index}
          actor={event.actor}
        />
        {!isLast && <ActivityTimelineConnector index={index} />}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0 pb-5 -mt-0.5">
        {/* Row 1: Actor · Action · Timestamp */}
        <div className="flex items-baseline justify-between gap-2">
          <p className="text-[13px] leading-snug min-w-0">
            <span className="font-semibold text-primary">
              {event.actor.name}
            </span>{" "}
            <span className="text-secondary">{event.title}</span>
          </p>
          <span className="text-[11px] text-muted shrink-0 tabular-nums">
            {formatRelativeTime(event.timestamp)}
          </span>
        </div>

        {/* Custom full-body override */}
        {renderer?.renderContent ? (
          renderer.renderContent(event)
        ) : (
          <>
            {/* Row 2: Status transition (old → new) */}
            {transition && (
              <div className="flex items-center gap-1.5 mt-1.5">
                <span className="inline-flex items-center rounded px-1.5 py-0.5 text-[11px] font-medium bg-card border border-border/60 text-secondary capitalize">
                  {transition.from}
                </span>
                <ArrowRight size={11} className="text-muted shrink-0" />
                <span className="inline-flex items-center rounded px-1.5 py-0.5 text-[11px] font-medium bg-accent-soft border border-accent/20 text-accent capitalize">
                  {transition.to}
                </span>
              </div>
            )}

            {/* Row 2b: Field-level diffs for update events */}
            {event.changes && Object.keys(event.changes).length > 0 && (
              <div className="flex flex-col gap-1.5 mt-2">
                {Object.entries(event.changes).map(([field, diff]) => (
                  <div
                    key={field}
                    className="flex items-baseline gap-1.5 text-[11px] flex-wrap"
                  >
                    <span className="text-muted shrink-0">
                      {fieldLabels?.[field] ?? field}:
                    </span>
                    <span className="inline-flex items-center rounded px-1.5 py-0.5 font-medium bg-card border border-border/60 text-secondary">
                      {diff.from}
                    </span>
                    <ArrowRight
                      size={10}
                      className="text-muted shrink-0 self-center"
                    />
                    <span className="inline-flex items-center rounded px-1.5 py-0.5 font-medium bg-accent-soft border border-accent/20 text-accent">
                      {diff.to}
                    </span>
                  </div>
                ))}
              </div>
            )}

            {/* Row 3: Description */}
            {event.description && (
              <p className="text-[12px] text-muted mt-1 leading-relaxed line-clamp-2">
                {event.description}
              </p>
            )}

            {/* Row 4: Extra metadata pills (custom or default) */}
            {renderer?.renderMetadata && event.metadata
              ? renderer.renderMetadata(event.metadata)
              : extraMeta.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-1.5">
                    {extraMeta.map(([key, value]) => (
                      <span
                        key={key}
                        className="inline-flex items-center gap-1 rounded-md bg-card px-2 py-0.5 text-[11px] border border-border/60 text-secondary"
                      >
                        <span className="text-muted capitalize">{key}:</span>
                        {value}
                      </span>
                    ))}
                  </div>
                )}
          </>
        )}
      </div>
    </motion.div>
  );
}
